import { Form } from '@mairistem/composants';
import type { Bordereau, Mouvement } from '@mairistem/comptabilite';
import { Combobox } from '@mairistem/finances-utils';
import type { DropdownOption } from '@mairistem/liste';
import { isEmpty, isNil } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import type BaseProfilEcritureMouvement from '../../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';
import CODE_BHM from '../../../../constants/mouvements/codeBHM';
import TYPE_PIECE from '../../../../constants/mouvements/typePiece';
import {
	getBordereau,
	getBordereaux,
} from '../../../../services/piecesComptables/bordereaux/bordereaux';

interface BordereauSelectProps {
	value: number | string;
	identifiant: number | string;
	mouvement: Mouvement;
	profilEcriture?: BaseProfilEcritureMouvement;
	onChange: (data: Bordereau) => void;
	onCreateBordereau?: () => Promise<Bordereau> | undefined;
	disabled?: boolean;
	/** Type des bordereaux proposés (ex. 02, RE pour une annulation ou un rejet) : sinon celui de la pièce. */
	typePes?: string;
	[key: string]: any;
}

const ADD = 'add';

const BORDEREAU_ADD = {
	'data-testid': 'bordereauAdd',
	key: ADD,
	text: 'Créer un bordereau',
	value: ADD,
} as const;

const BordereauSelect = ({
	value,
	mouvement,
	profilEcriture,
	onChange,
	onCreateBordereau,
	disabled = false,
	typePes,
	...rest
}: BordereauSelectProps) => {
	const [bordereaux, setBordereaux] = useState<DropdownOption[]>([]);
	const [isBordereauxLoading, setIsBordereauxLoading] =
		useState<boolean>(false);

	// Charger le bordereau sur lequel on est
	useEffect(() => {
		if (disabled && mouvement?.codeBHM === CODE_BHM.VALUE.HISTORISE) {
			setIsBordereauxLoading(true);
			getBordereau(mouvement?.identifiantBordereau)
				.then((data) => {
					setBordereaux([
						{
							'data-testid': `bordereau${data?.identifiant}`,
							key: data?.identifiant,
							text: `${data?.codeBHM}/${data?.numero}(${data?.sens}) ${data?.libelle}`,
							value: data?.identifiant,
						},
					]);
				})
				.catch(() => {
					toast.error('Impossible de récupérer le bordereau historisé');
				})
				.finally(() => {
					setIsBordereauxLoading(false);
				});
		}
	}, [mouvement?.codeBHM, mouvement?.identifiantBordereau, disabled]);

	// Charger la liste des bordereaux disponibles
	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de +
	useEffect(() => {
		if (mouvement?.sens && !disabled && (profilEcriture || typePes)) {
			setIsBordereauxLoading(true);
			const searchParams = new URLSearchParams();

			searchParams.append(
				'codeBHM',
				!isNil(mouvement?.codeBHM) &&
					mouvement.codeBHM !== CODE_BHM.VALUE.HISTORISE
					? mouvement?.codeBHM
					: CODE_BHM.VALUE.BROUILLARD,
			);

			if (typePes) {
				searchParams.append('typePes', typePes);
			} else if (
				!isEmpty(profilEcriture?.typeBordereau) ||
				!isEmpty(mouvement?.bordereau?.typePes)
			) {
				searchParams.append(
					'typePes',
					mouvement?.bordereau?.typePes ?? profilEcriture?.typeBordereau,
				);
			} else {
				searchParams.append('typePes', TYPE_PIECE.DEPENSE.ORDINAIRE);
			}
			searchParams.append(
				'identifiantEntite',
				mouvement?.identifiantEntite?.toString(),
			);
			searchParams.append(
				'identifiantExercice',
				mouvement?.identifiantExercice?.toString(),
			);

			searchParams.append('sens', mouvement?.sens);
			searchParams.append('all', 'true');

			getBordereaux(searchParams)
				.then(({ data }) => {
					setBordereaux([
						...data.map((bordereau) => ({
							'data-testid': `bordereau${bordereau?.identifiant}`,
							data: bordereau,
							key: bordereau?.identifiant,
							text: `${bordereau?.codeBHM}/${bordereau?.numero}(${bordereau?.sens}) ${bordereau?.libelle}`,
							value: bordereau?.identifiant,
						})),
						BORDEREAU_ADD,
					]);
				})
				.catch(() => {
					toast.error('Impossible de récupérer les bordereaux');
				})
				.finally(() => {
					setIsBordereauxLoading(false);
				});
		}
	}, [
		mouvement?.bordereau?.typePes,
		mouvement?.codeBHM,
		mouvement?.identifiantEntite,
		mouvement?.identifiantExercice,
		mouvement?.sens,
		profilEcriture?.typeBordereau,
		mouvement?.identifiant,
		disabled,
		typePes,
	]);

	const handleChangeBordereau = useCallback(
		async (_e, { options, value: selectedValue }) => {
			if (selectedValue === ADD) {
				if (onCreateBordereau) {
					try {
						const result = await onCreateBordereau();
						// Vérifier que le résultat est bien un objet Bordereau et pas void
						if (
							!isNil(result) &&
							typeof result === 'object' &&
							'identifiant' in result
						) {
							const newBordereau = result as Bordereau;
							setBordereaux((old) => [
								{
									data: newBordereau,
									'data-testid': `bordereau${newBordereau.identifiant}`,
									key: newBordereau.identifiant,
									text: `${newBordereau.codeBHM}/${newBordereau.numero}(${newBordereau.sens}) ${newBordereau.libelle}`,
									value: newBordereau.identifiant,
								},
								...old,
							]);

							// Mettre à jour la valeur sélectionnée
							onChange(newBordereau);
						}
					} catch (_error) {
						toast.error('Erreur lors de la création du bordereau');
					}
				}
			} else {
				const selectedBordereau = options.find(
					(option) => option.value === selectedValue,
				);
				if (selectedBordereau?.data) {
					onChange(selectedBordereau.data);
				}
			}
		},
		[onChange, onCreateBordereau],
	);

	return (
		<Form.Field
			control={Combobox}
			disabled={disabled}
			name="identifiantBordereau"
			onChange={handleChangeBordereau}
			options={bordereaux}
			selection
			value={value}
			loading={isBordereauxLoading}
			{...rest}
		/>
	);
};

export default BordereauSelect;
